import { createFileRoute, Link } from "@tanstack/react-router";
import { Fragment, useState } from "react";
import { AppIcon } from "~/components/app-icon";
import { InnerCta } from "~/components/inside/inner-cta";
import { PageIntro, PageLabel } from "~/components/inside/page-intro";
import { PageScene } from "~/components/inside/page-scene";
import { PlainList } from "~/components/inside/plain-list";
import { SegmentedFilter } from "~/components/inside/segmented-filter";
import { SheetArtwork } from "~/components/inside/sheets";
import { usePageEngagement } from "~/hooks/use-page-engagement";
import { publicCacheHeaders } from "~/lib/cache-headers";
import {
  type CaseCategory,
  getCaseFilters,
  getCaseTools,
  getCategoryLabel,
  getFitPoints,
  getPainCases,
} from "~/lib/constants/pain-points";
import { plural } from "~/lib/plural";
import { seo } from "~/lib/seo";
import {
  generateStructuredDataGraph,
  getPainPointsPageSchemas,
} from "~/lib/structured-data";
import * as m from "~/paraglide/messages.js";

export const Route = createFileRoute("/(public)/pain-points/")({
  component: PainPointsPage,
  head: () => {
    const seoData = seo({
      description: m.meta_pain_points_description(),
      keywords:
        "business process automation, AI customer support automation, AI sales automation, AI lead generation automation, AI document processing, AI data extraction, internal AI assistant, Lindevo",
      path: "/pain-points",
      title: m.meta_pain_points_title(),
    });
    const structuredData = generateStructuredDataGraph(
      getPainPointsPageSchemas()
    );
    return {
      links: seoData.links,
      meta: seoData.meta,
      scripts: [
        {
          children: structuredData,
          type: "application/ld+json",
        },
      ],
    };
  },
  headers: publicCacheHeaders,
});

type Filter = "all" | CaseCategory;

/**
 * Six everyday places work gets stuck, each one shown as the artifact a system
 * would prepare rather than described in the abstract.
 *
 * The filter narrows by rendering rather than by hiding: "all" is the initial
 * state, so every card is in the server-rendered document for a crawler and
 * for a visitor without JavaScript.
 */
function PainPointsPage() {
  usePageEngagement({ pagePath: "/pain-points" });

  const [filter, setFilter] = useState<Filter>("all");
  const cases = getPainCases();
  const visible =
    filter === "all" ? cases : cases.filter((item) => item.category === filter);

  return (
    <>
      <PageScene variant="usecase">
        <div>
          <PageLabel>{m.pain_points_label()}</PageLabel>
          <h1>
            {m.pain_points_title_1()} <br />
            {m.pain_points_title_2()}
          </h1>
        </div>

        <div>
          <PageIntro size="sm">{m.pain_points_intro()}</PageIntro>
          {/* One picture of a toolchain: the group carries the name, the
              arrows between the marks are decoration, and each mark stays
              unlabelled so the row announces once. */}
          <div
            aria-label={m.pain_points_tools_label()}
            className="case-tools"
            role="img"
          >
            {getCaseTools().map((tool, index) => (
              <Fragment key={tool}>
                {index > 0 ? <span aria-hidden="true">→</span> : null}
                <AppIcon name={tool} />
              </Fragment>
            ))}
          </div>
        </div>
      </PageScene>

      <section
        aria-label={m.pain_points_browser_label()}
        className="case-browser wrap"
      >
        <div className="case-toolbar">
          <SegmentedFilter
            label={m.pain_points_filter_label()}
            onChange={setFilter}
            options={getCaseFilters()}
            value={filter}
          />
          <p aria-live="polite" className="filter-count" role="status">
            {plural(visible.length, {
              one: m.pain_points_count_one,
              other: m.pain_points_count_other,
              zero: m.pain_points_count_zero,
            })}
          </p>
        </div>

        <div className="case-grid">
          {visible.map((item) => (
            <article className="case-card" id={item.id} key={item.id}>
              <div className="case-art">
                <SheetArtwork art={item.art} />
              </div>

              <div className="case-copy">
                <span className="case-category">
                  {getCategoryLabel(item.category)}
                </span>
                <h2>{item.title}</h2>
                <p>{item.body}</p>

                <details>
                  <summary>{m.pain_points_details_summary()}</summary>
                  <p>{item.detail.body}</p>
                  <Link hash={item.detail.link.hash} to={item.detail.link.to}>
                    {item.detail.link.label}
                  </Link>
                </details>
              </div>
            </article>
          ))}
        </div>

        <p className="case-disclaimer">{m.pain_points_disclaimer()}</p>
      </section>

      <section className="fit-band wrap">
        <div>
          <h2>
            {m.pain_points_fit_title_1()} <br />
            {m.pain_points_fit_title_2()}
          </h2>
          <p>{m.pain_points_fit_body()}</p>
        </div>
        <PlainList items={getFitPoints()} />
      </section>

      <InnerCta
        lede={m.pain_points_cta_lede()}
        note={m.pain_points_cta_note()}
        title={m.pain_points_cta_title()}
      />
    </>
  );
}
